/**
 * build-tree.ts — czysty model widoku dla `OrgTreeFilter` (drzewo struktury organizacyjnej
 * z checkboxami do zawężania listy wniosków po pracowniku).
 *
 * Wszystko tutaj jest funkcją czystą (zero Reacta, zero zapytań) — dzięki temu logika
 * trójstanu jednostki, zaznaczania kaskadowego i wyszukiwania ma własne testy jednostkowe,
 * a komponent zostaje cienką warstwą renderu.
 *
 * Model: jednostki zagnieżdżone (`children`) + pracownicy podpięci pod swoją jednostkę
 * (`employees`). Pracownicy bez jednostki — ORAZ pracownicy wskazujący jednostkę, której nie
 * ma w drzewie firmy (np. jednostka skasowana albo z innej firmy w trybie „Wszystkie firmy")
 * — trafiają do pseudo-grupy „Bez działu" doklejanej NA KOŃCU listy korzeni. Bez tego
 * kubełka część osób zniknęłaby z panelu i filtr po cichu nie dałby się ustawić.
 */

import { normalizeSearch } from '@/lib/search-normalize';
import type { OrgUnitTreeNode } from '@/lib/use-tenant-people';

/** Id pseudo-grupy „Bez działu" — nie jest to prawdziwy `org_units.id`. */
export const UNASSIGNED_UNIT_ID = '__org-tree-filter:unassigned__';

export interface OrgTreeEmployeeNode {
  id: string;
  name: string;
}

export interface OrgTreeUnitNode {
  id: string;
  name: string;
  children: OrgTreeUnitNode[];
  employees: OrgTreeEmployeeNode[];
}

/** Trójstan checkboxa jednostki, wyliczany z bieżącego zaznaczenia pracowników. */
export type UnitCheckState = 'checked' | 'indeterminate' | 'unchecked';

/**
 * Minimalny kształt pracownika, którego potrzebuje model — świadomie strukturalny (a nie
 * `EmployeeListItem`), żeby testy nie musiały budować pełnego wiersza listingu.
 */
export interface OrgTreeEmployeeInput {
  id: string;
  firstName: string;
  lastName: string;
  orgUnitId: string | null;
}

export interface BuildOrgTreeInput {
  units: OrgUnitTreeNode[];
  employees: OrgTreeEmployeeInput[];
  /** Etykieta pseudo-grupy pracowników bez jednostki (i18n — helper nie zna `t`). */
  unassignedLabel: string;
}

function employeeName(employee: OrgTreeEmployeeInput): string {
  return [employee.firstName, employee.lastName].filter(Boolean).join(' ').trim() || employee.id;
}

function byName(a: OrgTreeEmployeeNode, b: OrgTreeEmployeeNode): number {
  return a.name.localeCompare(b.name, 'pl');
}

function byUnitOrder(a: OrgUnitTreeNode, b: OrgUnitTreeNode): number {
  const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;
  return a.name.localeCompare(b.name, 'pl');
}

/** Płaska, posortowana alfabetycznie lista pracowników — używana też przez fallback bez drzewa. */
export function buildEmployeeNodes(employees: OrgTreeEmployeeInput[]): OrgTreeEmployeeNode[] {
  return employees.map((e) => ({ id: e.id, name: employeeName(e) })).sort(byName);
}

function collectUnitIdsFromApi(units: OrgUnitTreeNode[], acc: Set<string>): void {
  for (const unit of units) {
    acc.add(unit.id);
    collectUnitIdsFromApi(unit.children ?? [], acc);
  }
}

/** Buduje model widoku: jednostki zagnieżdżone + pracownicy + pseudo-grupa „Bez działu". */
export function buildOrgTree({
  units,
  employees,
  unassignedLabel,
}: BuildOrgTreeInput): OrgTreeUnitNode[] {
  const knownUnitIds = new Set<string>();
  collectUnitIdsFromApi(units, knownUnitIds);

  const byUnit = new Map<string, OrgTreeEmployeeInput[]>();
  const unassigned: OrgTreeEmployeeInput[] = [];
  for (const employee of employees) {
    const unitId = employee.orgUnitId;
    if (unitId && knownUnitIds.has(unitId)) {
      const bucket = byUnit.get(unitId) ?? [];
      bucket.push(employee);
      byUnit.set(unitId, bucket);
    } else {
      unassigned.push(employee);
    }
  }

  const mapUnit = (unit: OrgUnitTreeNode): OrgTreeUnitNode => ({
    id: unit.id,
    name: unit.name,
    children: [...(unit.children ?? [])].sort(byUnitOrder).map(mapUnit),
    employees: buildEmployeeNodes(byUnit.get(unit.id) ?? []),
  });

  const roots = [...units].sort(byUnitOrder).map(mapUnit);

  if (unassigned.length > 0) {
    roots.push({
      id: UNASSIGNED_UNIT_ID,
      name: unassignedLabel,
      children: [],
      employees: buildEmployeeNodes(unassigned),
    });
  }

  return roots;
}

/** Wszystkie id pracowników w poddrzewie jednostki (ona sama + wszyscy potomkowie). */
export function collectEmployeeIds(node: OrgTreeUnitNode): string[] {
  const out: string[] = node.employees.map((e) => e.id);
  for (const child of node.children) out.push(...collectEmployeeIds(child));
  return out;
}

/** Wszystkie id jednostek w poddrzewach (do wymuszonego rozwinięcia przy wyszukiwaniu). */
export function collectUnitIds(nodes: OrgTreeUnitNode[]): string[] {
  const out: string[] = [];
  for (const node of nodes) {
    out.push(node.id);
    out.push(...collectUnitIds(node.children));
  }
  return out;
}

/**
 * Trójstan jednostki: „zaznaczona" gdy WSZYSCY jej pracownicy (łącznie z podjednostkami) są
 * w zaznaczeniu, „częściowa" gdy część, „pusta" gdy nikt. Jednostka bez ani jednego
 * pracownika jest zawsze „pusta" (inaczej `0 === 0` czyniłoby ją zaznaczoną na starcie).
 */
export function deriveUnitCheckState(
  node: OrgTreeUnitNode,
  selectedIds: ReadonlySet<string>,
): UnitCheckState {
  const ids = collectEmployeeIds(node);
  if (ids.length === 0) return 'unchecked';
  let hit = 0;
  for (const id of ids) if (selectedIds.has(id)) hit += 1;
  if (hit === 0) return 'unchecked';
  return hit === ids.length ? 'checked' : 'indeterminate';
}

/** Zaznaczenie/odznaczenie CAŁEGO poddrzewa jednostki. Zwraca nową listę id (bez duplikatów). */
export function toggleUnitSelection(
  node: OrgTreeUnitNode,
  selectedIds: readonly string[],
  next: boolean,
): string[] {
  const subtree = new Set(collectEmployeeIds(node));
  if (!next) return selectedIds.filter((id) => !subtree.has(id));
  const merged = new Set(selectedIds);
  for (const id of subtree) merged.add(id);
  return [...merged];
}

/** Zaznaczenie/odznaczenie pojedynczego pracownika. */
export function toggleEmployeeSelection(
  employeeId: string,
  selectedIds: readonly string[],
  next: boolean,
): string[] {
  if (!next) return selectedIds.filter((id) => id !== employeeId);
  if (selectedIds.includes(employeeId)) return [...selectedIds];
  return [...selectedIds, employeeId];
}

function filterNode(node: OrgTreeUnitNode, needle: string): OrgTreeUnitNode | null {
  // Trafienie w nazwę jednostki zostawia CAŁE poddrzewo — user szukający „Serwis" chce
  // zobaczyć wszystkich z tego działu, nie tylko tych, których nazwisko brzmi jak dział.
  if (normalizeSearch(node.name).includes(needle)) return node;

  const children = node.children
    .map((child) => filterNode(child, needle))
    .filter((child): child is OrgTreeUnitNode => child !== null);
  const employees = node.employees.filter((e) => normalizeSearch(e.name).includes(needle));

  if (children.length === 0 && employees.length === 0) return null;
  return { ...node, children, employees };
}

/**
 * Filtruje drzewo po nazwie jednostki lub pracownika (bez diakrytyki). Przodkowie trafienia
 * ZOSTAJĄ w wyniku (inaczej wynik byłby płaską listą bez kontekstu działu) — komponent
 * rozwija wszystkie węzły, które przetrwały filtr.
 */
export function filterOrgTree(nodes: OrgTreeUnitNode[], query: string): OrgTreeUnitNode[] {
  const needle = normalizeSearch(query.trim());
  if (needle === '') return nodes;
  return nodes
    .map((node) => filterNode(node, needle))
    .filter((node): node is OrgTreeUnitNode => node !== null);
}

/** Filtr płaskiej listy pracowników (fallback bez drzewa) — ten sam sposób porównania. */
export function filterEmployeeNodes(
  nodes: OrgTreeEmployeeNode[],
  query: string,
): OrgTreeEmployeeNode[] {
  const needle = normalizeSearch(query.trim());
  if (needle === '') return nodes;
  return nodes.filter((n) => normalizeSearch(n.name).includes(needle));
}
